import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Pencil } from 'lucide-react';
import axios from 'axios';
import cacheStorage from '../../../utils/cacheStorage';
import './ImportPackagesModal.css';

// ─── Package fields the API accepts ───────────────────────────────────────────
const PACKAGE_FIELDS = [
    { key: 'tracking_code', label: 'Tracking Code', required: true },
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'customer_phone', label: 'Customer Phone' },
    { key: 'delivery_address', label: 'Delivery Address' },
    { key: 'status', label: 'Status' },
    { key: 'date_start', label: 'Start Date' },
    { key: 'date_end', label: 'End Date' },
    { key: 'total_weight', label: 'Weight (kg)' },
    { key: 'total_volume', label: 'Volume (m³)' },
    { key: 'courier_id', label: 'Courier ID' },
];

// ─── Auto-detect which package field a CSV header maps to ─────────────────────
function autoDetectField(csvHeader) {
    const h = csvHeader.toLowerCase().replace(/[\s_-]/g, '');
    if (h.includes('tracking') || h === 'code') return 'tracking_code';
    if (h.includes('customername') || (h.includes('customer') && h.includes('name'))) return 'customer_name';
    if (h.includes('phone') || h.includes('mobile')) return 'customer_phone';
    if (h.includes('address') || h.includes('delivery')) return 'delivery_address';
    if (h === 'status') return 'status';
    if (h.includes('start') || h === 'datestart') return 'date_start';
    if (h.includes('end') || h === 'dateend') return 'date_end';
    if (h.includes('weight')) return 'total_weight';
    if (h.includes('volume') || h.includes('m3')) return 'total_volume';
    if (h.includes('courier')) return 'courier_id';
    return '__skip__';
}

// ─── CSV parser (handles quoted fields) ───────────────────────────────────────
function parseCSV(text) {
    const rawLines = text.split(/\r?\n/).filter(l => l.trim() !== '');
    if (rawLines.length < 2) throw new Error('CSV must have at least a header row and one data row.');

    const parseLine = (line) => {
        const values = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                inQuotes = !inQuotes;
            } else if (ch === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
            } else {
                current += ch;
            }
        }
        values.push(current.trim());
        return values;
    };

    const headers = parseLine(rawLines[0]).map(h => h.replace(/^"|"$/g, '').trim());
    const rows = rawLines.slice(1).map(line => {
        const vals = parseLine(line);
        return headers.reduce((obj, header, i) => {
            obj[header] = (vals[i] || '').replace(/^"|"$/g, '').trim();
            return obj;
        }, {});
    });

    return { headers, rows };
}

// ─── Excel parser (requires: npm install xlsx) ─────────────────────────────────
async function parseExcel(file) {
    let XLSX;
    try {
        XLSX = await import('xlsx');
    } catch {
        throw new Error(
            'Excel support requires the xlsx package.\nRun: npm install xlsx'
        );
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
                resolve({ headers, rows });
            } catch (err) {
                reject(new Error('Failed to parse Excel file: ' + err.message));
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsBinaryString(file);
    });
}

// ─── Build the Package payload from a raw CSV row + column mapping ─────────────
function buildPackagePayload(row, mapping) {
    const get = (field) => {
        const csvCol = Object.keys(mapping).find(col => mapping[col] === field);
        return csvCol ? (row[csvCol] || '').trim() : '';
    };

    const strField = (val) => val ? { String: val, Valid: true } : { String: '', Valid: false };
    const courierVal = parseInt(get('courier_id'), 10);

    return {
        tracking_code: get('tracking_code'),
        status: get('status') || 'Pending',
        courier_id: !isNaN(courierVal) && courierVal > 0
            ? { Int64: courierVal, Valid: true }
            : { Int64: 0, Valid: false },
        total_weight: parseFloat(get('total_weight')) || 0,
        total_volume: parseFloat(get('total_volume')) || 0,
        date_start: strField(get('date_start')),
        date_end: strField(get('date_end')),
        delivery_address: strField(get('delivery_address')),
        customer_name: strField(get('customer_name')),
        customer_phone: strField(get('customer_phone')),
        delivery_location_x: { Float64: 0, Valid: false },
        delivery_location_y: { Float64: 0, Valid: false },
    };
}

// ─── Steps ────────────────────────────────────────────────────────────────────
const STEP_UPLOAD  = 'upload';
const STEP_MAP     = 'map';
const STEP_RESULT  = 'result';
const PREVIEW_LIMIT = 100;

function EditableCell({ value, onChange }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef();

    useEffect(() => { setDraft(value); }, [value]);
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const commit = () => {
        setEditing(false);
        if (draft !== value) onChange(draft);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                className="editable-cell-input"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={e => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
                }}
            />
        );
    }

    return (
        <span
            className="editable-cell-display"
            onClick={() => setEditing(true)}
            title="Click to edit"
        >
            {value || <span className="editable-cell-empty">—</span>}
        </span>
    );
}
export default function ImportPackagesModal({ isOpen, onClose, onImportSuccess }) {
    const [step, setStep] = useState(STEP_UPLOAD);
    const [isDragging, setIsDragging] = useState(false);
    const [parseError, setParseError] = useState('');
    const [headers, setHeaders] = useState([]);
    const [editableRows, setEditableRows] = useState([]);
    const [mapping, setMapping] = useState({});   // { csvHeader: packageField | '__skip__' }
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef();

    const reset = () => {
        setStep(STEP_UPLOAD);
        setParseError('');
        setHeaders([]);
        setEditableRows([]);
        setMapping({});
        setImporting(false);
        setImportResult(null);
    };

    const handleClose = () => { reset(); onClose(); };

    // ── File handling ────────────────────────────────────────────────────────────
    const processFile = useCallback(async (file) => {
        setParseError('');
        try {
            let parsed;
            if (file.name.endsWith('.csv') || file.type === 'text/csv') {
                const text = await file.text();
                parsed = parseCSV(text);
            } else if (file.name.match(/\.xlsx?$/i)) {
                parsed = await parseExcel(file);
            } else {
                setParseError('Unsupported file type. Please upload a .csv or .xlsx file.');
                return;
            }

            if (parsed.rows.length === 0) {
                setParseError('The file has no data rows.');
                return;
            }

            const autoMapping = {};
            parsed.headers.forEach(h => { autoMapping[h] = autoDetectField(h); });

            setHeaders(parsed.headers);
            setEditableRows(parsed.rows.slice(0, PREVIEW_LIMIT));
            setMapping(autoMapping);
            setStep(STEP_MAP);
        } catch (err) {
            setParseError(err.message);
        }
    }, []);

    const onFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
        e.target.value = '';
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    // // ── Mapping change ───────────────────────────────────────────────────────────
    // const handleMappingChange = (csvHeader, newField) => {
    //     setMapping(prev => ({ ...prev, [csvHeader]: newField }));
    // };


    const handleCellEdit = (rowIndex, csvHeader, newValue) => {
        setEditableRows(prev => {
            const next = [...prev];
            next[rowIndex] = { ...next[rowIndex], [csvHeader]: newValue };
            return next;
        });
    };

    const addRow = () => {
        const emptyRow = headers.reduce((obj, h) => { obj[h] = ''; return obj; }, {});
        setEditableRows(prev => [...prev, emptyRow]);
    };

    const removeRow = (i) => setEditableRows(prev => prev.filter((_, idx) => idx !== i));

    // ── Validation before import ─────────────────────────────────────────────────
    const missingRequired = PACKAGE_FIELDS
        .filter(f => f.required)
        .filter(f => !Object.values(mapping).includes(f.key));

    // ── Import ───────────────────────────────────────────────────────────────────
    const handleImport = async () => {
        if (missingRequired.length > 0) return;
        setImporting(true);

        const payloads = editableRows.map(row => buildPackagePayload(row, mapping));

        try {
            const token = cacheStorage.getItem('access_token');
            const response = await axios.post('/api/packages/bulk', payloads, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setImportResult(response.data);
            setStep(STEP_RESULT);
        } catch (err) {
            const msg = err.response?.data || err.message || 'Import failed.';
            setImportResult({ imported: 0, failed: editableRows.length, errors: [msg] });
            setStep(STEP_RESULT);
        } finally {
            setImporting(false);
        }
    };
    const visibleHeaders = headers.filter(h => mapping[h] !== '__skip__');
    if (!isOpen) return null;

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <div className="import-modal-overlay">
            <div className="import-modal-content import-modal-wide">

            {/* Header */}
                <div className="import-modal-header">
                    <div>
                        <h2>Import Packages</h2>
                        <p className="import-modal-subtitle">
                            {step === STEP_UPLOAD && 'Upload a CSV or Excel file to bulk-add packages'}
                            {step === STEP_MAP && `${editableRows.length} row${editableRows.length !== 1 ? 's' : ''} loaded — edit any cell before importing`}
                            {step === STEP_RESULT && 'Import complete'}
                        </p>
                    </div>
                    <button className="import-modal-close" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Step indicator */}
                <div className="import-steps">
                    {[
                        { key: STEP_UPLOAD, label: '1. Upload' },
                        { key: STEP_MAP, label: '2. Map & Edit' },
                        { key: STEP_RESULT, label: '3. Results' },
                    ].map(({ key, label }) => (
                        <div
                            key={key}
                            className={`import-step ${step === key ? 'active' : ''} ${
                                (step === STEP_MAP && key === STEP_UPLOAD) ||
                                (step === STEP_RESULT)
                                    ? 'done'
                                    : ''
                            }`}
                        >
                            {label}
                        </div>
                    ))}
                </div>

                {/* ── STEP 1: Upload ── */}
                {step === STEP_UPLOAD && (
                    <div className="import-modal-body">
                        <div
                            className={`import-dropzone ${isDragging ? 'dragging' : ''}`}
                            onClick={() => fileInputRef.current.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={onDrop}
                        >
                            <Upload size={40} className="import-dropzone-icon" />
                            <p className="import-dropzone-title">Drop your file here, or <span>browse</span></p>
                            <p className="import-dropzone-hint">Supports .csv and .xlsx files</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={onFileSelected}
                            />
                        </div>

                        {parseError && (
                            <div className="import-error-box">
                                <AlertCircle size={16} />
                                <span>{parseError}</span>
                            </div>
                        )}

                        <div className="import-template-hint">
                            <FileText size={14} />
                            <span>
                Expected columns:{' '}
                                <code>tracking_code, customer_name, customer_phone, delivery_address, status, date_start, date_end, total_weight, total_volume, courier_id</code>
              </span>
                        </div>
                    </div>
                )}

                {/* ── STEP 2: Column Mapping ── */}
                {/* ── STEP 2: Edit ── */}
                {step === STEP_MAP && (
                    <div className="import-modal-body">
                        <div className="import-preview-section">
                            <div className="import-preview-header-row">
                                <div className="import-preview-label-group">
                                    <Pencil size={13} />
                                    <p className="import-preview-label">
                                        Editable Preview
                                        <span className="import-preview-count">
                            &nbsp;·&nbsp;{editableRows.length} row{editableRows.length !== 1 ? 's' : ''}
                                            {editableRows.length === PREVIEW_LIMIT ? ` (first ${PREVIEW_LIMIT})` : ''}
                        </span>
                                    </p>
                                </div>
                                <button className="btn-add-row" onClick={addRow}>+ Add Row</button>
                            </div>

                            <div className="import-editable-scroll">
                                <table className="import-editable-table">
                                    <thead>
                                    <tr>
                                        <th className="row-num-col">#</th>
                                        {visibleHeaders.map(h => (
                                            <th key={h}>
                                                {PACKAGE_FIELDS.find(f => f.key === mapping[h])?.label || h}
                                                {PACKAGE_FIELDS.find(f => f.key === mapping[h])?.required && <span className="required-star"> *</span>}
                                            </th>
                                        ))}
                                        <th className="row-action-col"></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {editableRows.map((row, ri) => (
                                        <tr key={ri} className="editable-row">
                                            <td className="row-num-cell">{ri + 1}</td>
                                            {visibleHeaders.map(h => (
                                                <td key={h} className="editable-td">
                                                    <EditableCell
                                                        value={String(row[h] ?? '')}
                                                        onChange={val => handleCellEdit(ri, h, val)}
                                                    />
                                                </td>
                                            ))}
                                            <td className="row-action-col">
                                                <button className="btn-remove-row" onClick={() => removeRow(ri)} title="Remove row">×</button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                {editableRows.length === 0 && (
                                    <div className="editable-empty-state">No rows — click <strong>+ Add Row</strong> to add data manually.</div>
                                )}
                            </div>
                        </div>

                        <div className="import-modal-actions">
                            <button className="btn-import-back" onClick={reset}>← Back</button>
                            <button
                                className="btn-import-submit"
                                onClick={handleImport}
                                disabled={importing || editableRows.length === 0}
                            >
                                {importing
                                    ? `Importing ${editableRows.length} package${editableRows.length !== 1 ? 's' : ''}…`
                                    : `Import ${editableRows.length} Package${editableRows.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── STEP 3: Results ── */}
                {step === STEP_RESULT && importResult && (
                    <div className="import-modal-body">
                        <div className="import-result-summary">
                            <div className="import-result-card success">
                                <CheckCircle size={24} />
                                <div>
                                    <p className="import-result-number">{importResult.imported}</p>
                                    <p className="import-result-label">Successfully imported</p>
                                </div>
                            </div>
                            {importResult.failed > 0 && (
                                <div className="import-result-card error">
                                    <AlertCircle size={24} />
                                    <div>
                                        <p className="import-result-number">{importResult.failed}</p>
                                        <p className="import-result-label">Failed</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {importResult.errors && importResult.errors.length > 0 && (
                            <div className="import-error-list">
                                <p className="import-error-list-title">Errors:</p>
                                <ul>
                                    {importResult.errors.map((e, i) => (
                                        <li key={i}>{e}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="import-modal-actions">
                            <button
                                className="btn-import-back"
                                onClick={reset}
                            >
                                Import Another File
                            </button>
                            <button
                                className="btn-import-submit"
                                onClick={() => {
                                    handleClose();
                                    if (importResult.imported > 0) onImportSuccess();
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}